import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent } from '../ui/card';
import { NO_SWIPE_ATTRIBUTE } from '../../hooks/useSwipeTabs';
import { CHART_TOOLTIP_STYLE } from '../../utils/stats-utils';
import type { Climb } from '../../types/climb';

/**
 * Literal colours, not theme variables — the convention the app's other charts
 * already follow (see GradeDistributionChart), and for a reason this one
 * learned the hard way: `--muted` is 96% lightness in the light theme and 15%
 * in the dark one, which is the card's own background in both, so bars painted
 * with it are invisible.
 */
const COLORS = {
  done: '#3b82f6',
  todo: '#9ca3af',
};

/** One bar per grade, so bars stay legible whether there are three or twenty. */
const MAX_BAR_WIDTH = 18;

interface GradeBar {
  grade: string;
  /** Position on the scale — what the bars are ordered by, not the name. */
  value: number;
  done: number;
  todo: number;
}

interface CragGradeChartProps {
  /** One discipline's climbs — see the note on mixing scales below. */
  climbs: Climb[];
  /** What the bars are counting, already translated. */
  title: string;
}

/**
 * How hard the cliff is, and how much of it the reader has done: one bar per
 * grade, split into what they have sent and what is left.
 *
 * The two segments are always both there. An earlier version dropped the split
 * when the reader had sent nothing and painted the single remaining bar in the
 * "done" colour — so the same bar meant two different things depending on who
 * was looking, which is worse than a column of honest grey.
 *
 * One discipline at a time, on purpose. A crag can grade its boulders on Font
 * and its routes on the French scale, and the two are different rulers — laid
 * on one axis they would sort by a number that means nothing across them. The
 * info panel renders this once per discipline that has anything in it.
 *
 * Only grades that exist here get a bar: an outdoor crag's range is whatever
 * happens to be bolted, and padding the axis out to the whole system would bury
 * it in empty columns.
 */
export function CragGradeChart({ climbs, title }: CragGradeChartProps) {
  const { t } = useTranslation();

  const data = useMemo<GradeBar[]>(() => {
    const bars = new Map<string, GradeBar>();

    for (const climb of climbs) {
      // A line nobody has graded yet says nothing about how hard the place is.
      if (!climb.difficulty) continue;

      const bar = bars.get(climb.difficulty)
        ?? { grade: climb.difficulty, value: climb.difficulty_value ?? 0, done: 0, todo: 0 };

      if (climb.user_topped) bar.done += 1;
      else bar.todo += 1;

      bars.set(climb.difficulty, bar);
    }

    return [...bars.values()].sort((a, b) => a.value - b.value || a.grade.localeCompare(b.grade));
  }, [climbs]);

  if (data.length === 0) return null;

  const label = (key: string) => t(key === 'done' ? 'cragInfo.chartSent' : 'cragInfo.chartRest');

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <h3 className="text-sm font-medium">{title}</h3>
        {/* A cliff can run twenty grades wide, so the plot keeps a minimum width
            and scrolls sideways inside the card rather than crushing the labels.
            Flagged so dragging along it scrolls the plot instead of leaving the
            tab it is on. */}
        <div className="overflow-x-auto" {...{ [NO_SWIPE_ATTRIBUTE]: '' }}>
          <div style={{ minWidth: Math.max(280, data.length * 44) }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="grade" tick={{ fontSize: 11 }} interval={0} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value, name) => [value as number, label(String(name))]} />
                <Legend formatter={(key) => label(String(key))} wrapperStyle={{ fontSize: 12 }} />

                {/* Done at the bottom, so the coloured part grows out of the
                    axis the way a progress bar does. */}
                <Bar dataKey="done" name="done" stackId="grade" fill={COLORS.done} maxBarSize={MAX_BAR_WIDTH} />
                <Bar
                  dataKey="todo"
                  name="todo"
                  stackId="grade"
                  fill={COLORS.todo}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={MAX_BAR_WIDTH}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
